import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, TrendingUp, ThumbsUp, ThumbsDown, Sparkles, Lock, Unlock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommunityPointsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const { theme } = useTheme();
  const [futureFeatures, setFutureFeatures] = useState([]);
  const [myVotes, setMyVotes] = useState({});

  useEffect(() => {
    fetchFutureFeatures();
    if (isAuthenticated) {
      fetchMyVotes();
    }
  }, [isAuthenticated]);

  const fetchFutureFeatures = async () => {
    try {
      const response = await axios.get(`${API}/community/future-features`);
      setFutureFeatures(response.data.features || []);
    } catch (error) {
      console.error('Error fetching features:', error);
    }
  };

  const fetchMyVotes = async () => {
    try {
      const response = await axios.get(`${API}/community/my-votes`, {
        headers: getAuthHeader()
      });
      const votesObj = {};
      response.data.votes?.forEach(v => {
        votesObj[v.feature_id] = v.vote_type;
      });
      setMyVotes(votesObj);
    } catch (error) {
      console.error('Error fetching votes:', error);
    }
  };

  const voteFeature = async (featureId, voteType) => {
    if (!isAuthenticated) {
      toast.error('Please login to vote');
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/community/vote-feature`, {
        feature_id: featureId,
        vote_type: voteType
      }, {
        headers: getAuthHeader()
      });

      setMyVotes({...myVotes, [featureId]: voteType});
      toast.success('Vote recorded!');
      fetchFutureFeatures();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const earnMethods = [
    { action: 'Create journal entry', points: 5, icon: '📝', phase: 'Live' },
    { action: 'Upload practice video', points: 10, icon: '🎥', phase: 'Live' },
    { action: 'Write blog (< 1000 words)', points: 25, icon: '✍️', phase: 'Live' },
    { action: 'Write blog (1000+ words)', points: 50, icon: '📚', phase: 'Live' },
    { action: 'Answer question', points: 20, icon: '💬', phase: 'Phase 1' },
    { action: 'Get answer accepted', points: 50, icon: '✅', phase: 'Phase 1' },
    { action: 'Get upvoted', points: 5, icon: '👍', phase: 'Phase 1' },
    { action: 'Achieve a goal', points: 100, icon: '🎯', phase: 'Phase 2' },
    { action: 'Complete coaching session', points: 25, icon: '👨‍🏫', phase: 'Phase 3' },
    { action: '7-day practice streak', points: 50, icon: '🔥', phase: 'Future' }
  ];

  const unlockTiers = [
    { points: 100, feature: 'Featured Profile Badge', icon: '⭐' },
    { points: 500, feature: 'Skip Approval Queue', icon: '⚡' },
    { points: 1000, feature: 'Priority Questions', icon: '🎤' },
    { points: 2000, feature: 'Advanced Analytics', icon: '📊' },
    { points: 5000, feature: 'Lifetime Pro Membership', icon: '👑' }
  ];

  const userPoints = user?.community_points || 0;

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/')} className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4`}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Community Points System</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Earn points, unlock features, and shape the platform</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Your Points */}
        {isAuthenticated && (
          <Card className={`glass-card mb-8 text-center ${theme === 'light' ? 'bg-gradient-to-br from-yellow-50 to-orange-50' : 'bg-gradient-to-br from-yellow-900/20 to-orange-900/20'}`}>
            <div className="py-8">
              <Award className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
              <div className="text-6xl font-bold gradient-text mb-2">{userPoints}</div>
              <div className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Your Community Points</div>
              <p className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Keep contributing to earn more!</p>
            </div>
          </Card>
        )}

        {/* How to Earn Points */}
        <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
          <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>How to Earn Points</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {earnMethods.map((method, idx) => (
              <div key={idx} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{method.icon}</span>
                    <div>
                      <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{method.action}</div>
                      <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>{method.phase}</div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-green-500">+{method.points}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Unlock Tiers */}
        <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
          <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Unlock Features</h2>
          <div className="space-y-4">
            {unlockTiers.map((tier, idx) => {
              const unlocked = userPoints >= tier.points;
              return (
                <div key={idx} className={`p-4 rounded-lg flex items-center justify-between ${unlocked ? (theme === 'dark' ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200') : (theme === 'dark' ? 'bg-white/5' : 'bg-gray-100')}`}>
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-full ${unlocked ? 'bg-green-500' : (theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300')} flex items-center justify-center text-2xl`}>
                      {unlocked ? <Unlock className="w-6 h-6 text-white" /> : <Lock className="w-6 h-6 text-gray-500" />}
                    </div>
                    <div>
                      <div className={`font-bold text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{tier.feature}</div>
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{tier.points} points required</div>
                    </div>
                  </div>
                  <span className="text-3xl">{tier.icon}</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Future Features - Vote! */}
        <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
          <div className="flex items-center space-x-2 mb-6">
            <Sparkles className="w-6 h-6 text-purple-400" />
            <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Future Features - Vote Now!</h2>
          </div>
          <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Help us prioritize! Vote for features you want to see next.
          </p>
          
          <div className="space-y-4">
            {futureFeatures.map((feature) => (
              <div key={feature.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className={`font-bold text-lg mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{feature.title}</h3>
                    <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{feature.description}</p>
                    <div className="flex items-center space-x-4 text-xs">
                      <span className={`px-2 py-1 rounded ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                        {feature.phase}
                      </span>
                      <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}>
                        {feature.upvotes - feature.downvotes} net votes
                      </span>
                    </div>
                  </div>
                  
                  {isAuthenticated && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => voteFeature(feature.id, 'upvote')}
                        className={`p-2 rounded-lg transition-colors ${
                          myVotes[feature.id] === 'upvote'
                            ? 'bg-green-500 text-white'
                            : (theme === 'dark' ? 'bg-white/10 hover:bg-green-500/20 text-gray-400' : 'bg-gray-200 hover:bg-green-100 text-gray-600')
                        }`}
                      >
                        <ThumbsUp className="w-5 h-5" />
                      </button>
                      <span className={`text-lg font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                        {feature.upvotes}
                      </span>
                      <button
                        onClick={() => voteFeature(feature.id, 'downvote')}
                        className={`p-2 rounded-lg transition-colors ${
                          myVotes[feature.id] === 'downvote'
                            ? 'bg-red-500 text-white'
                            : (theme === 'dark' ? 'bg-white/10 hover:bg-red-500/20 text-gray-400' : 'bg-gray-200 hover:bg-red-100 text-gray-600')
                        }`}
                      >
                        <ThumbsDown className="w-5 h-5" />
                      </button>
                      <span className={`text-lg font-bold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                        {feature.downvotes}
                      </span>
                    </div>
                  )}
                </div>
                
                <Progress value={(feature.upvotes / Math.max(feature.upvotes + feature.downvotes, 1)) * 100} className="h-2" />
              </div>
            ))}
          </div>
          
          {!isAuthenticated && (
            <div className="text-center mt-6">
              <Button onClick={() => navigate('/login')} className="bg-gradient-to-r from-purple-500 to-pink-500">
                Login to Vote on Features
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
